import { softwareApi } from '../api/systemRegisterApi';
import { unwrapData } from './responseHelpers';
import { softwareToRequest } from './softwareMappers';
import {
  buildChangeManagements,
  buildDataSources,
  buildIncidents,
  buildIntegrations,
  buildMonitoringReviews,
} from './wizardPayloadBuilder';
import { detailToWizardState } from './wizardStateMapper';

const COLLECTION_CONFIG = {
  incidents: {
    requestKey: 'incidents',
    wizardKey: 'incidentEscalation',
    buildPayload: (entries) => buildIncidents({ incidentEscalation: { entries } }),
  },
  monitoringReviews: {
    requestKey: 'monitoringReviews',
    wizardKey: 'monitoringReview',
    buildPayload: (entries) => buildMonitoringReviews({ monitoringReview: { entries } }),
  },
  changeManagements: {
    requestKey: 'changeManagements',
    wizardKey: 'changeManagement',
    buildPayload: (entries) => buildChangeManagements({ changeManagement: { entries } }),
  },
  integrations: {
    requestKey: 'integrations',
    wizardKey: 'integration',
    buildPayload: (entries) => buildIntegrations({ integration: { entries } }),
  },
};

function preserveClientKeys(previousEntries, nextEntries) {
  return nextEntries.map((entry, index) => ({
    ...entry,
    key: previousEntries[index]?.key || entry.key,
  }));
}

/**
 * Persist a wizard nested collection (incidents / monitoring reviews / changes)
 * onto an existing software record, then return remapped wizard entries with DB ids.
 */
export async function persistWizardNestedCollection(softwareId, collectionType, entries) {
  if (!softwareId) {
    throw new Error('Save system identification first so this record can be linked to the software.');
  }

  const config = COLLECTION_CONFIG[collectionType];
  if (!config) {
    throw new Error(`Unknown collection type: ${collectionType}`);
  }

  const detailRes = await softwareApi.get(softwareId);
  const detail = unwrapData(detailRes);
  if (!detail?.id) {
    throw new Error('Software not found.');
  }

  const request = softwareToRequest(detail);
  request[config.requestKey] = config.buildPayload(entries);
  if (collectionType === 'integrations') {
    request.integratedWithOtherSystems = true;
  }

  const updateRes = await softwareApi.update(softwareId, request);
  const updated = unwrapData(updateRes);
  const wizard = detailToWizardState(updated);
  const nextEntries = wizard?.[config.wizardKey]?.entries ?? [];
  return preserveClientKeys(entries, nextEntries);
}

/** Patch scalar software fields (e.g. integratedWithOtherSystems) without wiping nested collections. */
export async function persistWizardSoftwareFields(softwareId, patch = {}) {
  if (!softwareId) {
    throw new Error('Save system identification first so this record can be linked to the software.');
  }
  const detailRes = await softwareApi.get(softwareId);
  const detail = unwrapData(detailRes);
  if (!detail?.id) {
    throw new Error('Software not found.');
  }
  const request = softwareToRequest(detail);
  Object.assign(request, patch);
  const updateRes = await softwareApi.update(softwareId, request);
  return unwrapData(updateRes);
}

/** Persist Data & Model Information fields onto the software without wiping other collections. */
export async function persistArtificialIntelligenceDetails(softwareId, dataModel, riskImpact = {}) {
  if (!softwareId) {
    throw new Error('Save system identification first so this record can be linked to the software.');
  }
  const detailRes = await softwareApi.get(softwareId);
  const detail = unwrapData(detailRes);
  if (!detail?.id) {
    throw new Error('Software not found.');
  }
  const request = softwareToRequest(detail);
  const meta = {
    dataInformationId: detail.dataInformation?.id ?? null,
    dataSourceIds: (detail.dataSources ?? []).map((d) => d.id).filter((id) => id != null),
  };
  request.dataInformation = {
    ...(meta.dataInformationId != null ? { id: meta.dataInformationId } : {}),
    personalData: Boolean(dataModel?.personalData),
    trainingDataOrigin: dataModel?.trainingDataOrigin || undefined,
    modelType: dataModel?.modelType || undefined,
    sensitiveDataCategories: Array.isArray(dataModel?.sensitiveDataCategories)
      ? dataModel.sensitiveDataCategories
      : [],
  };
  request.dataSources = buildDataSources(dataModel, meta);
  request.riskAssessment = {
    ...(request.riskAssessment ?? {}),
    usesArtificialIntelligence: true,
    knownBias: Boolean(riskImpact?.knownBias),
    biasDescription: (riskImpact?.biasDescription || '').trim() || undefined,
  };
  const updateRes = await softwareApi.update(softwareId, request);
  return unwrapData(updateRes);
}
